import React, { useState, useEffect } from "react";
import Layout from "../../components/ui/Layout/Layout";
import Pageheader from "../../components/ui/PageHeader/Pageheader";
import Button from "../../components/ui/Button/Button";
import Snackbar from "../../components/ui/Snackbar/Snackbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./setting.css";

const DEFAULT_CATEGORIES = [
  { value: "electronics", label: "Electronics" },
  { value: "mobiles", label: "Mobiles & Accessories" },
  { value: "computers", label: "Computer & Parts" },
  { value: "home", label: "Home Appliances" },
  { value: "gaming", label: "Gaming" },
  { value: "office", label: "Office Electronics" },
  { value: "audio", label: "Audio & Music" },
  { value: "others", label: "Others" },
];

const STORAGE_KEY = "custom_categories";

const loadCategories = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
  } catch {
    return DEFAULT_CATEGORIES;
  }
};

const SettingPage = () => {
  const [categories, setCategories] = useState(loadCategories);
  const [newLabel, setNewLabel] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [snackbar, setSnackbar] = useState(null);
  const [activeTab, setActiveTab] = useState("categories");

  // Get role from localStorage
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const role = user?.role || "staff";
  const isOwner = role === "admin" || role === "owner";

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
    window.__customCategories = categories;
    // Trigger storage event for other tabs
    window.dispatchEvent(new Event("storage"));
  }, [categories]);

  const slugify = (str) =>
    str.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return setSnackbar({ message: "Category name cannot be empty", type: "error" });
    const value = slugify(label);
    if (categories.find((c) => c.value === value)) {
      return setSnackbar({ message: "This category already exists", type: "error" });
    }
    setCategories([...categories, { value, label }]);
    setNewLabel("");
    setSnackbar({ message: `"${label}" added successfully`, type: "success" });
  };

  const handleDelete = (index) => {
    const cat = categories[index];
    setCategories(categories.filter((_, i) => i !== index));
    setSnackbar({ message: `"${cat.label}" removed`, type: "success" });
  };

  const startEdit = (index) => {
    setEditIndex(index);
    setEditLabel(categories[index].label);
  };

  const saveEdit = () => {
    const label = editLabel.trim();
    if (!label) return setSnackbar({ message: "Category name cannot be empty", type: "error" });
    const value = slugify(label);
    setCategories(categories.map((c, i) => i === editIndex ? { value, label } : c));
    setEditIndex(null);
    setEditLabel("");
    setSnackbar({ message: "Category updated", type: "success" });
  };

  const resetToDefault = () => {
    setCategories(DEFAULT_CATEGORIES);
    setSnackbar({ message: "Categories reset to defaults", type: "success" });
  };

  return (
    <Layout>
      <Pageheader title="Settings" showBtn={false} />

      <div className="settings-page">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <button className={`settings-tab ${activeTab === "categories" ? "active" : ""}`} onClick={() => setActiveTab("categories")}>
            <FontAwesomeIcon icon="tags" /> Categories
          </button>
          <button className={`settings-tab ${activeTab === "about" ? "active" : ""}`} onClick={() => setActiveTab("about")}>
            <FontAwesomeIcon icon="circle-info" /> About
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">

          {activeTab === "categories" && (
            <div className="settings-section">
              <div className="settings-section-header">
                <h3>Inventory Categories</h3>
                <p className="settings-desc">
                  {isOwner
                    ? "Add, rename, or remove categories used across inventory and POS."
                    : "These are the categories used in your inventory and POS."}
                  {!isOwner && (
                    <span className="no-permission-note">
                      <FontAwesomeIcon icon="lock" /> Only the owner can edit categories
                    </span>
                  )}
                </p>
              </div>

              <div className="category-list">
                {categories.length === 0 && (
                  <div className="empty-categories">No categories yet. Add one below.</div>
                )}
                {categories.map((cat, index) => (
                  <div className="category-item" key={cat.value + index}>
                    {editIndex === index ? (
                      <div className="category-edit-row">
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditIndex(null); }}
                          autoFocus
                          className="category-edit-input"
                          maxLength="30"
                        />
                        <button className="cat-btn save" onClick={saveEdit} title="Save"><FontAwesomeIcon icon="check" /></button>
                        <button className="cat-btn cancel" onClick={() => setEditIndex(null)} title="Cancel"><FontAwesomeIcon icon="xmark" /></button>
                      </div>
                    ) : (
                      <>
                        <span className="category-label">
                          <FontAwesomeIcon icon="tag" className="tag-icon" />
                          {cat.label}
                          <span className="category-slug">{cat.value}</span>
                        </span>
                        {isOwner && (
                          <div className="category-actions">
                            <button className="cat-btn edit" onClick={() => startEdit(index)} title="Edit"><FontAwesomeIcon icon="pencil" /></button>
                            <button className="cat-btn delete" onClick={() => handleDelete(index)} title="Delete"><FontAwesomeIcon icon="trash" /></button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Add new — owner only */}
              {isOwner && (
                <>
                  <div className="add-category-row">
                    <input
                      type="text"
                      placeholder="New category name..."
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                      className="add-category-input"
                      maxLength="30"
                    />
                    <Button variant="primary" onClick={handleAdd}>
                      <FontAwesomeIcon icon="plus" /> Add
                    </Button>
                  </div>
                  <div className="settings-footer-actions">
                    <Button variant="text" onClick={resetToDefault}>
                      <FontAwesomeIcon icon="rotate-left" /> Reset to Default
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "about" && (
            <div className="settings-section">
              <h3>About</h3>
              <p className="settings-desc">Customer Inventory Management System — manage your inventory, sales, staff and customers.</p>
              <div className="about-info">
                <div className="about-row"><span>Version</span><span>1.0.0</span></div>
                <div className="about-row"><span>Your Role</span><span style={{ textTransform: "capitalize" }}>{role}</span></div>
                <div className="about-row"><span>Currency</span><span>₹ Indian Rupee</span></div>
                <div className="about-row"><span>Categories</span><span>{categories.length} active</span></div>
              </div>
            </div>
          )}

        </div>
      </div>

      {snackbar && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />
      )}
    </Layout>
  );
};

export default SettingPage;
